define((require) => {
    "use strict";

    const Queue = require("shared/services/sync/Queue");

    /**
     * @class {SequentialQueue}
     * A <i>sequential queue</i> describes the order of sync methods, e.g. "save()", with respect to a number
     * of target entities, 1-n, where the invocation of the i'th target sync method will complete before the sync
     * target i+1'th will commence. It is thus (normally) a heterogeneous queue with respect to target identity,
     * though the sync method is normally, but not required to, homogeneous. <p>
     *
     * In addition, each target referenced by a sequential queue may already have a "target queue", i.e. because of
     * currently pending sync requests for some sync method on the target instance. For each such target, say the i'th
     * one in the sequential queue, the current sync request will only commence after the target queue associated with
     * the target instance is empty; this may thus block the i+1'th to n sync requests in the current (and future)
     * sequential queue. <p>
     *
     * Unless a result handler is supplied at construction time, the queue will resolve to each target synced, in
     * queuing order.
     *
     * @author Gunni Rode / <a href="http://www.zmags.com">Zmags</a>
     *
     * @see    ParallelQueue
     * @see    TargetQueue
     */
    return Queue.extend("SequentialQueue", {

        _enqueue(enqueue, options, targets, args) {
            // We have to create target queues immediately for each queued request, for all indices, so future sync
            // requests can know which targets are currently being synced/queued. Each created request thus stores a
            // reference to the target queue used to sync the target in question, and each sync request for said
            // queue will be tagged with a reference to this sequential queue. This ensures the target queue will
            // block until allowed progress by this queue (or some other sequential queue also using the same
            // target queue before/after)...
            return enqueue(targets, (queue, target) => { // this queue!
                const tq = queue.manager.queue(target); // must create target queue at enqueue time for possible future requests!
                const promise = tq.enqueue(options, args, queue); // "tq" blocked by this ("queue")

                return {
                    promise() {
                        return promise;
                    },
                    sync() {
                        // Remove the reference to to this sequential queue from the pending request so it can
                        // be allowed to continue on dequeuing...
                        promise.entry.removeBlocker(queue);
                        // Start sync for the target queue, if not already...
                        tq.sync();
                        return promise;
                    },
                    // cancel from our entry and forward, since followers will depend on it...
                    cancel() {
                        return tq.cancelEntry ? tq.cancelEntry(promise.entry) : null;
                    },
                    toString() {
                        return `${queue.id}:${queue.size()}[${promise.entry}]`;
                    }
                };
            });
        }
    });
});



// WEBPACK FOOTER //
// ./src/shared/services/sync/SequentialQueue.js